import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useNumericInput from "../../../hooks/useNumericInput";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import DealAPI from "../../../api/promo/dealApi";
import SubmitButton from "../../../components/partials/global/SubmitButton";
import { getBadgeColor } from "../../../helper/Helper";

const INITIAL_FORM = {
  productLabel: "",
  variantLabel: "",
  originalPrice: "",
  dealPrice: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  totalQuantity: "",
  soldQuantity: 0,
  dealDate: "",
  status: "scheduled",
};

function Edit() {
  useNumericInput();

  const { id } = useParams();
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const { showLoader, hideLoader } = useLoader();

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);

  // ─── Permission flags ───────────────────────────────────
  const isScheduled = formData.status === "scheduled";
  const isActive = formData.status === "active";
  const isReadOnly =
    formData.status === "expired" || formData.status === "cancelled";

  // ─── Fetch deal ─────────────────────────────────────────
  useEffect(() => {
    fetchDealDetails();
  }, [id]);

  const fetchDealDetails = async () => {
    showLoader();
    try {
      const res = await DealAPI.getSingleDetails(id);
      if (res.code === 200) {
        const d = res.data;
        setFormData({
          productLabel: d.productId?.title || "—",
          variantLabel: d.variantId?.title || "",
          originalPrice: d.originalPrice || "",
          dealPrice: d.dealPrice || "",
          discountType: d.discountType || "PERCENTAGE",
          discountValue: d.discountValue || "",
          totalQuantity: d.totalQuantity || "",
          soldQuantity: d.soldQuantity || 0,
          dealDate: d.dealDate || "",
          status: d.status || "scheduled",
        });
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      hideLoader();
    }
  };

  // ─── Auto-calc ──────────────────────────────────────────
  const calculateDealPrice = (op, type, dv) => {
    op = Number(op);
    dv = Number(dv);
    if (!op || !dv) return "";
    if (type === "PERCENTAGE") {
      if (dv >= 1 && dv <= 99) return Math.round(op - (op * dv) / 100);
    } else {
      if (dv > 0 && dv < op) return op - dv;
    }
    return "";
  };

  const updateField = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleOriginalPriceChange = (value) => {
    updateField({
      originalPrice: value,
      dealPrice: calculateDealPrice(
        value,
        formData.discountType,
        formData.discountValue,
      ),
    });
  };

  const handleDiscountChange = (value) => {
    if (value !== "") {
      const num = Number(value);
      if (formData.discountType === "PERCENTAGE" && (num < 1 || num > 99))
        return;
      if (formData.discountType === "FLAT" && num < 1) return;
    }
    updateField({
      discountValue: value,
      dealPrice: calculateDealPrice(
        formData.originalPrice,
        formData.discountType,
        value,
      ),
    });
  };

  const handleDiscountTypeChange = (value) => {
    updateField({ discountType: value, discountValue: "", dealPrice: "" });
  };

  // ─── Validation ─────────────────────────────────────────
  const validate = () => {
    const newErrors = {};

    if (isScheduled) {
      if (!formData.originalPrice)
        newErrors.originalPrice = "Original price is required";
      if (!formData.dealPrice) newErrors.dealPrice = "Deal price is required";
      if (!formData.discountValue)
        newErrors.discountValue = "Discount value is required";
      if (!formData.dealDate) newErrors.dealDate = "Deal date is required";
    }

    if (!formData.totalQuantity) {
      newErrors.totalQuantity = "Quantity is required";
    } else if (Number(formData.totalQuantity) < formData.soldQuantity) {
      newErrors.totalQuantity = `Cannot be less than sold (${formData.soldQuantity})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Active deal mein sirf totalQuantity bhejo
    const payload = isActive
      ? { totalQuantity: Number(formData.totalQuantity) }
      : {
          originalPrice: Number(formData.originalPrice),
          dealPrice: Number(formData.dealPrice),
          discountType: formData.discountType,
          discountValue: Number(formData.discountValue),
          totalQuantity: Number(formData.totalQuantity),
          dealDate: formData.dealDate,
        };

    setIsLoading(true);
    showLoader();
    try {
      const res = await DealAPI.updateDetails(id, payload);
      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  // ─── Render ─────────────────────────────────────────────
  return (
    <div className="content">
      <div className="page-header">
        <div className="page-title">
          <h4>Edit Deal</h4>
          <p>
            Status:{" "}
            <span className={`badge bg-${getBadgeColor(formData.status)}`}>
              {formData.status?.toUpperCase()}
            </span>
          </p>
        </div>
      </div>

      {/* Status-based info banner */}
      {isActive && (
        <div className="alert alert-warning">
          ⚠️ This deal is <strong>currently active</strong>. You can only
          increase the quantity. Pricing and date cannot be modified.
        </div>
      )}
      {isReadOnly && (
        <div className="alert alert-secondary">
          🔒 This deal is <strong>{formData.status}</strong>. View only — no
          edits allowed.
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h4>Deal Details</h4>
        </div>
        <div className="card-body profile-body">
          <form method="post" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-lg-4 mb-3">
                <label className="form-label">Product</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={formData.productLabel}
                  readOnly
                />
                <small className="text-muted">
                  Product cannot be changed. Cancel and create a new deal
                  instead.
                </small>
              </div>

              {formData.variantLabel && (
                <div className="col-lg-4 mb-3">
                  <label className="form-label">Variant</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formData.variantLabel}
                    readOnly
                  />
                </div>
              )}

              <div className="col-lg-4 mb-3">
                <label className="form-label">
                  Deal Date{" "}
                  {isScheduled && <span className="text-danger">*</span>}
                </label>
                <input
                  type="date"
                  min={today}
                  className={`form-control ${!isScheduled ? "bg-light" : ""}`}
                  value={formData.dealDate}
                  onChange={(e) => updateField({ dealDate: e.target.value })}
                  readOnly={!isScheduled}
                />
                {errors.dealDate && (
                  <span className="text-danger errors">{errors.dealDate}</span>
                )}
              </div>

              <div className="col-lg-3 mb-3">
                <label className="form-label">
                  Original Price{" "}
                  {isScheduled && <span className="text-danger">*</span>}
                </label>
                <input
                  type="number"
                  className={`form-control ${!isScheduled ? "bg-light" : ""}`}
                  value={formData.originalPrice}
                  onChange={(e) => handleOriginalPriceChange(e.target.value)}
                  readOnly={!isScheduled}
                />
                {errors.originalPrice && (
                  <span className="text-danger errors">
                    {errors.originalPrice}
                  </span>
                )}
              </div>

              <div className="col-lg-3 mb-3">
                <label className="form-label">
                  Discount Type{" "}
                  {isScheduled && <span className="text-danger">*</span>}
                </label>
                <select
                  className="form-control"
                  value={formData.discountType}
                  onChange={(e) => handleDiscountTypeChange(e.target.value)}
                  disabled={!isScheduled}
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FLAT">Flat Amount (₹)</option>
                </select>
              </div>

              <div className="col-lg-3 mb-3">
                <label className="form-label">
                  {formData.discountType === "PERCENTAGE"
                    ? "Percentage (%)"
                    : "Flat (₹)"}{" "}
                  {isScheduled && <span className="text-danger">*</span>}
                </label>
                <input
                  type="number"
                  className={`form-control ${!isScheduled ? "bg-light" : ""}`}
                  value={formData.discountValue}
                  onChange={(e) => handleDiscountChange(e.target.value)}
                  readOnly={!isScheduled}
                />
                {errors.discountValue && (
                  <span className="text-danger errors">
                    {errors.discountValue}
                  </span>
                )}
              </div>

              <div className="col-lg-3 mb-3">
                <label className="form-label">Deal Price (Auto)</label>
                <input
                  type="text"
                  className="form-control bg-success-subtle"
                  value={formData.dealPrice}
                  readOnly
                />
              </div>

              <div className="col-lg-3 mb-3">
                <label className="form-label">
                  Total Quantity <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control only-numeric ${isReadOnly ? "bg-light" : ""}`}
                  value={formData.totalQuantity}
                  onChange={(e) =>
                    updateField({ totalQuantity: e.target.value })
                  }
                  readOnly={isReadOnly}
                />
                {formData.soldQuantity > 0 && (
                  <small className="text-muted">
                    Sold: {formData.soldQuantity} / {formData.totalQuantity}
                  </small>
                )}
                {errors.totalQuantity && (
                  <span className="text-danger errors">
                    {errors.totalQuantity}
                  </span>
                )}
              </div>
            </div>

            {/* Submit only if editable */}
            {!isReadOnly && (
              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Update Deal"
                    loadingText="Updating..."
                  />
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// Helper for status badge color

export default Edit;
